import type { ColumnFilterOption } from '@/components/ui';
import type { LineProjectStatus } from '@/lib/types';

/**
 * "Projects added / not added" on the quote and invoice lists — the ONE place
 * its four words are chosen.
 *
 * ⚠️ **ONE MODULE, FOUR MOUNT POINTS.** The desktop cell and the sub-`md`
 * stacked card on both `/quotes` and `/invoices` read these labels, and both
 * pages hand `LINE_PROJECT_FILTER_OPTIONS` to `useTableFilters`. Two copies
 * would agree the day they were written and drift on the first correction, and
 * what would drift is whether the word in the CELL means the same thing as the
 * word in the FILTER — a row reading "Not added" that the "Not added" option
 * does not return, which is invisible from any single screen.
 *
 * The rendering half is `components/documents/LineProjectStatusCell.tsx`; the
 * split follows this app's layer convention (`lib/docusign.ts` beside
 * `components/docusign/`), not a second decision point.
 *
 * ══ WHY FOUR WORDS FOR A QUESTION ASKED AS A YES/NO ═══════════════════════
 *
 * The request was *"a column saying projects added/not added"*. The data is not
 * a boolean, because a project is chosen PER LINE ITEM (backend migrations
 * 067-069): a project names exactly one product and a document sells many, so
 * one project on the header could never describe more than one line.
 *
 * So a document can be HALF assigned, and it can have nothing to assign to:
 *
 *     all        every line names a project        -> "Added"
 *     some       at least one does, one does not   -> "Partly added"
 *     none       lines, and not one has a project  -> "Not added"
 *     no_lines   no line items at all              -> "No line items"
 *
 * ⚠️ **THE OWNER'S TWO WORDS ARE BOTH HERE.** "Added" and "Not added" are the
 * ends of the scale and say exactly what was asked for; the other two are the
 * states that would otherwise have to be filed under one of them. Folding
 * `some` into "Added" reports a half-finished document as done; folding it into
 * "Not added" reports finished work as not started. Both are silent.
 *
 * ⚠️ **"No line items" IS NOT A VARIANT OF "Not added", and the wording is what
 * keeps them apart.** A document with no lines has nothing to attach a project
 * to, so it is not outstanding work on this axis — sending somebody to it wastes
 * their time and makes the "Not added" count untrustworthy. It is not a
 * curiosity either: production held 32 quotes with not one line item between
 * them on 2026-08-31.
 *
 * ⛔ **NO COUNT, and none is available.** "2 of 5 lines" would be more
 * informative and this app cannot say it: the API answers the STATE, and a list
 * response carries no line items to count. Anything of that shape here would be
 * a number nobody computed.
 */

/**
 * ⚠️ ORDERED LEAST-COMPLETE FIRST, matching the server's own sort rank exactly
 * (`no_lines` 0 -> `all` 3). The filter popover renders this order, so a person
 * scanning it reads the same progression `?sort=lineProjectStatus&order=asc`
 * puts the rows in.
 *
 * Reordering this array changes only what the popover looks like — it CANNOT
 * change the sort, which is the server's — so the two would silently disagree.
 */
export const LINE_PROJECT_STATUSES: readonly LineProjectStatus[] = [
  'no_lines',
  'none',
  'some',
  'all',
  /*
   * ⚠️ APPENDED, and that is not an exception to the ordering rule above — its
   * place in the sequence genuinely is the end. The order runs from most work
   * outstanding to least, and this is a zero-outstanding state, so it belongs
   * beside `all`. The server's array is ordered identically and ITS index is the
   * sort rank, so these two must stay in step.
   */
  'no_projectable_lines',
];

const LABELS: Record<LineProjectStatus, string> = {
  no_lines: 'No line items',
  none: 'Not added',
  some: 'Partly added',
  all: 'Added',
  /*
   * ⚠️ NOT "Not applicable" and not "None needed". This says what is true of the
   * document — nothing on it is sold through projects — rather than ruling on
   * whether that is fine, which is the reader's judgement and not this column's.
   */
  no_projectable_lines: 'No projects apply',
};

/** True for a value the API actually returns. */
export function isLineProjectStatus(value: string): value is LineProjectStatus {
  return (LINE_PROJECT_STATUSES as readonly string[]).includes(value);
}

/**
 * The label for a raw value, for `ColumnSpec.format` and for the cell.
 *
 * ⚠️ An unrecognised value renders VERBATIM rather than as one of the four. A
 * value we do not know is newer than this branch, exactly as an unrecognised
 * Xero refusal code is — and printing the server's own word is honest, where
 * mapping it onto "Not added" would be this client inventing an answer.
 */
export function lineProjectStatusLabel(value: string): string {
  return isLineProjectStatus(value) ? LABELS[value] : value;
}

/**
 * The complete option list for the header funnel, in server rank order.
 *
 * ⚠️ **DECLARED RATHER THAN ACCUMULATED FROM THE LOADED ROWS**, which is what
 * `ColumnSpec.options` exists for. Left to derive itself, the popover would
 * offer only the states present on the pages somebody had already looked at — so
 * "Partly added" would be missing exactly when it is rare, which is exactly when
 * it is worth finding. On `/invoices` it is worse than that: nearly every
 * imported document reads `none`, so a derived list would often hold one option.
 */
export const LINE_PROJECT_FILTER_OPTIONS: ColumnFilterOption[] = LINE_PROJECT_STATUSES.map(
  (value) => ({ value, label: LABELS[value] }),
);
